//#region IMPORT
import React from 'react';
import * as XLSX from 'xlsx';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../Reducer/SnackbarReducer';

//#endregion

function ExcelUploadCommon({
  gridRef,
  columnDefs,
  fileInputRef,
  rowData,
  code = [],
  setChangedRowData,
  setDeletedRowData,
  checkboxFields = [],
  stripQuoteFields = [],
}) {
  const dispatch = useDispatch();
  //파일 변경 함수
  const FileChange = (event) => {
    const fileList = event.target.files;
    if (Object.keys(fileList).length === 0) return;

    const file = fileList[0]; // single file upload

    // 엑셀만 가능
    const fileExtension = file.name
      .slice(file.name.lastIndexOf('.'))
      .toLowerCase();
    const validExcelExtensions = ['.xls', '.xlsx'];

    if (!validExcelExtensions.includes(fileExtension)) {
      dispatch(
        showSnackbar({
          message: '엑셀파일만 가능합니다.',
          type: 'warning',
        })
      );
      return;
    }

    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = (e) => {
      const bstr = e.target.result; //string 파일

      const workBook = XLSX.read(bstr, {
        type: 'binary',
      });
      const workSheetName = workBook.SheetNames[0];

      const workSheet = workBook.Sheets[workSheetName];

      const fileData = XLSX.utils.sheet_to_json(workSheet, {
        header: 1,
        raw: false,
        cellDates: true,
      });
      const excludeFields = ['no'];
      const visibleColumns = columnDefs.filter(
        (col) => !col.hide && !excludeFields.includes(col.field)
      );

      // headerName(한글) → field(영문) 매핑
      const headerMap = {};
      visibleColumns.forEach((col) => {
        headerMap[col.headerName.trim()] = col.field.trim();
      });

      const excelHeaders = fileData[0]; // 엑셀 헤더 행
      // 1. 엑셀 헤더를 field로 변환
      const convertedHeaders = excelHeaders.map(
        (header) => headerMap[header?.trim()] || header?.trim() // 매핑되지 않은 헤더는 원본 그대로 유지
      );
      fileData[0] = convertedHeaders;
      // 2. 필수 field 리스트와 비교해 누락 확인
      const requiredFields = visibleColumns.map((col) => col.field);

      const missingFields = requiredFields.filter(
        (field) => !convertedHeaders.includes(field)
      );

      if (missingFields.length > 0) {
        dispatch(
          showSnackbar({
            open: true,
            type: 'error',
            //message: `엑셀 양식이 올바르지 않습니다. 누락된 항목: ${missingFields.join(', ')}`,
            message: `엑셀 양식이 올바르지 않습니다.`,
          })
        );
        return;
      }

      const gridData = fileData.slice(1);

      //엑셀에서는 headerName으로 들어와서 filed로 바꿔주는 작업
      for (let i = 0; i < columnDefs.length; i++) {
        for (let j = 0; j < columnDefs.length; j++) {
          if (columnDefs[i].headerName === fileData[0][j]) {
            fileData[0][j] = columnDefs[i].field;
            continue;
          }
        }
      }

      const rowsToAdd = PopulateGridData(gridData, fileData[0]);
      AppendToGridData(rowsToAdd);
    };
    reader.onerror = (error) => {
      console.log(error);
      // 파일 읽기 중 에러가 발생했을 때 실행될 코드
    };
  };
  // 그리드에 넣을 데이터 형식으로 변환 (공통: Date → 문자열, 숫자 변환만)
  const PopulateGridData = (gridData, headers) => {
    return gridData.map((row) => {
      const rowObject = {};
      headers.forEach((header, index) => {
        let value = row[index];

        if (value === null || value === undefined) {
          value = '';
        } else if (value instanceof Date) {
          const y = value.getFullYear();
          const m = String(value.getMonth() + 1).padStart(2, '0');
          const d = String(value.getDate()).padStart(2, '0');
          value = `${y}-${m}-${d}`;
        } else if (
          typeof value !== 'object' &&
          value !== '' &&
          !isNaN(value) &&
          !isNaN(parseFloat(value))
        ) {
          const numValue = Number(value);
          if (isFinite(numValue)) value = numValue;
        } else if (typeof value === 'object' && value !== null && Array.isArray(value.richText)) {
          value = (value.richText || []).map((item) => item?.text || '').join('') || '';
        } else {
          value = String(value ?? '').trim();
        }

        if (stripQuoteFields.length && stripQuoteFields.includes(header)) {
          value = String(value ?? '').replace(/^['"]+/, '');
        }
        if (checkboxFields.length && checkboxFields.includes(header)) {
          const strVal = String(value ?? '').trim();
          value =
            strVal === '✓' ||
            strVal.toUpperCase() === 'Y' ||
            strVal.toUpperCase() === 'Y.N'
              ? 'Y'
              : 'N';
        }

        rowObject[header] = value;
      });
      return rowObject;
    });
  };

  // 업로드 클릭 이벤트 (HEA_BASIC_02 패턴: code 키 기준 수정/신규/삭제 구분)
  const AppendToGridData = (rowsToAdd) => {
    const uploadedData = rowsToAdd.map((node, index) => ({
      ...node,
      excel: 'Y',
      i: index + 1,
      no: node.no || uuidv4(),
    }));

    if (code.length > 0 && rowData && rowData.length > 0) {
      // 키 문자열: code 필드값을 '|'로 연결 (예: id|employeeNo)
      const makeKeyString = (item) => {
        const key = code
          .map((k) => {
            const value = item[k];
            return value !== null && value !== undefined
              ? String(value).trim()
              : '';
          })
          .join('|');
        return key;
      };

      const existingDataMap = new Map();
      rowData.forEach((row) => {
        if (!row.deleteYn) {
          const key = makeKeyString(row);
          if (key) existingDataMap.set(key, row);
        }
      });

      const excelKeySet = new Set();
      uploadedData.forEach((row) => {
        const key = makeKeyString(row);
        if (key) excelKeySet.add(key);
      });

      // 변경 여부 비교: 엑셀에 있는 필드만 비교(서버 전용 필드 제외), falsy(undefined/null/'')는 동일 값으로 간주
      const excludeCompareFields = ['no', 'newYn', 'editYn', 'excel', 'deleteYn', 'i'];
      const toCompareValue = (v) => {
        if (v === undefined || v === null || v === '') return '';
        return String(v).trim();
      };
      const hasChanges = (existingRow, excelRow) => {
        const fields = Object.keys(excelRow).filter(
          (k) => k && !excludeCompareFields.includes(k)
        );
        for (const field of fields) {
          const a = toCompareValue(existingRow[field]);
          const b = toCompareValue(excelRow[field]);
          if (a !== b) return true;
        }
        return false;
      };

      const newRows = [];
      const updatedRows = [];
      const updatedKeys = new Set();

      for (const excelRow of uploadedData) {
        const key = makeKeyString(excelRow);
        if (!key) continue;

        if (existingDataMap.has(key)) {
          const existingRow = existingDataMap.get(key);
          if (hasChanges(existingRow, excelRow)) {
            const updatedRow = {
              ...existingRow,
              ...excelRow,
              editYn: 'Y',
              excel: 'Y',
              no: existingRow.no,
            };
            updatedRows.push(updatedRow);
            updatedKeys.add(key);
          }
        } else {
          newRows.push({
            ...excelRow,
            newYn: 'Y',
            excel: 'Y',
            no: excelRow.no || `new_${Date.now()}_${Math.random()}`,
          });
        }
      }

      // 엑셀에 없는 기존 키 → 삭제 예정
      const rowsToDelete = [];
      rowData.forEach((row) => {
        if (!row.deleteYn) {
          const key = makeKeyString(row);
          if (key && !excelKeySet.has(key)) {
            rowsToDelete.push({ ...row, deleteYn: 'Y' });
          }
        }
      });

      const updatedRowData = rowData.map((row) => {
        if (row.deleteYn) return row;
        const key = makeKeyString(row);
        if (key && updatedKeys.has(key)) {
          const updatedRow = updatedRows.find((ur) => makeKeyString(ur) === key);
          if (updatedRow) return updatedRow;
        }
        if (rowsToDelete.some((rd) => rd.no === row.no)) {
          return { ...row, deleteYn: 'Y' };
        }
        return row;
      });

      const finalRowData = [...updatedRowData, ...newRows];
      gridRef.current.api.setRowData(finalRowData);
      setChangedRowData(
        finalRowData.filter((row) => row.newYn === 'Y' || row.editYn === 'Y')
      );

      if (rowsToDelete.length > 0) {
        setDeletedRowData((prev) => {
          const existingDeleted = prev || [];
          const newDeleted = rowsToDelete.filter(
            (rd) => !existingDeleted.some((ed) => ed.no === rd.no)
          );
          return [...existingDeleted, ...newDeleted];
        });
      }

      dispatch(
        showSnackbar({
          message: '엑셀 업로드가 완료되었습니다.',
          type: 'success',
        })
      );
    } else {
      // code 필드가 없거나 기존 데이터가 없는 경우: 모든 데이터를 신규로 처리
      const newOnlyData = uploadedData.map((row) => ({
        ...row,
        newYn: 'Y',
        excel: 'Y',
      }));
      gridRef.current.api.setRowData(newOnlyData);
      setChangedRowData(newOnlyData);
      setDeletedRowData([]);
      dispatch(
        showSnackbar({
          message: '엑셀 업로드가 완료되었습니다.',
          type: 'success',
        })
      );
    }
  };
  return (
    <React.Fragment>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".xls, .xlsx, .cell"
        onChange={(e) => {
          FileChange(e);
          e.target.value = '';
        }}
      />
    </React.Fragment>
  );
}

export default ExcelUploadCommon;
